import * as React from "react";
import { LucideIcon } from "lucide-react";
import { Input } from "./input";
import { cn } from "../../../lib/utils";

interface FormInputProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "onBlur"> {
  name: string;
  icon?: LucideIcon;
  iconClassName?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  touched?: boolean;
  error?: string;
  errorClassName?: string;
  containerClassName?: string;
  showError?: boolean;
  rightElement?: React.ReactNode;
  validation?: {
    values: any;
    setFieldValue: (field: string, value: any) => void;
    setFieldTouched?: (field: string, touched: boolean) => void;
    touched: any;
    errors: any;
  };
}

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      name,
      icon: Icon,
      iconClassName,
      value: valueProp,
      onChange: onChangeProp,
      onBlur: onBlurProp,
      touched: touchedProp,
      error: errorProp,
      errorClassName,
      containerClassName,
      showError = true,
      className,
      rightElement,
      validation,
      ...props
    },
    ref
  ) => {
    // Use validation prop if available, otherwise use direct props (backward compatible)
    const value = validation?.values?.[name] ?? valueProp ?? "";
    const touched = validation?.touched?.[name] ?? touchedProp ?? false;
    const error = validation?.errors?.[name] ?? errorProp ?? "";

    const onChange = validation
      ? (e: React.ChangeEvent<HTMLInputElement>) => {
          validation.setFieldValue(name, e.target.value);
          onChangeProp?.(e);
        }
      : onChangeProp;

    const onBlur = validation
      ? () => {
          validation.setFieldTouched?.(name, true);
          onBlurProp?.();
        }
      : onBlurProp;

    const hasError = touched && Boolean(error);

    return (
      <div className={containerClassName}>
        <div className="relative form-input-wrapper">
          {Icon && (
            <Icon
              className={cn(
                "absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10 pointer-events-none form-input-icon",
                iconClassName
              )}
            />
          )}
          <Input
            // @ts-ignore - ref forwarding issue with TypeScript
            ref={ref}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className={cn(
              Icon && "pl-10",
              rightElement && "pr-12",
              hasError && "border-red-500",
              className
            )}
            {...props}
          />
          {rightElement && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 z-20">
              {rightElement}
            </div>
          )}
        </div>
        {showError && hasError && (
          <p className={cn("text-sm text-red-500 mt-2 tailwind_error_msg")}>
            {error}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = "FormInput";

export { FormInput };
